import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
  useContext,
} from "react";
import "./Main_Chart_Section.css";
import io from "socket.io-client";
import { Context } from "../../../../utils/context";
import AmChartsCandleChart from "./AmChartsCandleChart";
import Successfull_Modal from "../../../common_components/common_modal/successfull_modal/Successfull_Modal";
import Deposit_Lottie from "../../../animation/deposit_lottie/Deposit_Lottie";

/* ----------------------------- Helpers ----------------------------- */

const debounce = (fn, delay = 300) => {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), delay);
  };
};

const sanitizeCandle = (c) => ({
  time: Number(c.time),
  open: Math.max(0, Number(c.open ?? 0)),
  high: Math.max(0, Number(c.high ?? 0)),
  low: Math.max(0, Number(c.low ?? 0)),
  close: Math.max(0, Number(c.close ?? 0)),
  volume: Number(c.volume ?? 0),
});

/* ----------------------------- Component ----------------------------- */

const Main_Chart_Section = ({
  companyId,
  companyName,
  latestAmount = 0,
  setLatestAmount = () => {},
  setLatestData = () => {},
  setLatestCloses = () => {},
  setLatestBaseCloses = () => {},
}) => {
  const { SOCKET } = useContext(Context);
  const socketRef = useRef(null);

  const [timeframe, setTimeframe] = useState("1M");
  const [candleData, setCandleData] = useState([]);
  const [high24h, setHigh24h] = useState(null);
  const [low24h, setLow24h] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // console.log("Candle Data:", candleData);

  /* ---------------------- Timeframe Config ---------------------- */

  const TIMEFRAMES = useMemo(
    () => ({
      "1M": { intervalMin: 1 },
      "5M": { intervalMin: 5 },
      "15M": { intervalMin: 15 },
      "30M": { intervalMin: 30 },
      "1H": { intervalMin: 60 },
      "4H": { intervalMin: 240 },
      "1D": { intervalMin: 1440 },
    }),
    []
  );

  const currentIntervalSec = TIMEFRAMES[timeframe]?.intervalMin * 60 || 60;

  /* -------------------------- Socket Setup -------------------------- */

  useEffect(() => {
    if (!SOCKET) return;
    const socket = io(SOCKET, { transports: ["websocket"] });
    socketRef.current = socket;
    return () => socket.disconnect();
  }, [SOCKET]);

  /* --------------------- Real-time Candle Building --------------------- */

  useEffect(() => {
    const socket = socketRef.current;
    if (!socket || !companyId) return;

    const handler = (incoming) => {
      const points = Array.isArray(incoming) ? incoming : [incoming];

      setCandleData((prev) => {
        const map = new Map(prev.map((c) => [c.time, c]));

        let latestPrice = null;
        let prevPrice = null;

        points.forEach((p) => {
          if (!p?.Date) return;

          const tsSec = Math.floor(Number(p.Date) / 1000); // ms → sec
          const bucketTime = Math.floor(tsSec / currentIntervalSec) * currentIntervalSec;

          const price = Number(p.Close);
          const high = Number(p.High || price);
          const low = Number(p.Low || price);
          const open = Number(p.Open || price);
          const volume = Number(p.Volume || 0);

          latestPrice = price;

          if (map.has(bucketTime)) {
            const existing = map.get(bucketTime);
            map.set(bucketTime, {
              ...existing,
              high: Math.max(existing.high, high),
              low: Math.min(existing.low, low),
              close: price,
              volume: existing.volume + volume,
            });
          } else {
            map.set(bucketTime, sanitizeCandle({
              time: bucketTime,
              open,
              high,
              low,
              close: price,
              volume,
            }));
          }
        });

        // Update latest price states
        if (latestPrice !== null) {
          setLatestAmount(latestPrice);
          setLatestData((prev) => ({ ...prev, [companyId]: latestPrice }));
          setLatestCloses((prev) => ({ ...prev, [companyId]: latestPrice }));
        }

        // Optional: Update previous close if needed
        if (points.length > 1) {
          prevPrice = Number(points[points.length - 2].Close);
          setLatestBaseCloses((prev) => ({ ...prev, [companyId]: prevPrice }));
        }

        // Limit history to last ~500 candles to prevent memory growth
        const sorted = Array.from(map.values())
          .sort((a, b) => a.time - b.time)
          .slice(-500);

        return sorted;
      });

      // Update 24h high/low from the incoming points
      setHigh24h((prev) => {
        const newHighs = points.map((p) => Number(p.High || p.Close));
        return Math.max(prev || 0, ...newHighs);
      });
      setLow24h((prev) => {
        const newLows = points.map((p) => Number(p.Low || p.Close));
        const minLow = Math.min(...newLows);
        return prev === null ? minLow : Math.min(prev, minLow);
      });
    };

    socket.on(companyId, handler);
    return () => socket.off(companyId, handler);
  }, [
    companyId,
    timeframe,
    currentIntervalSec,
    setLatestAmount,
    setLatestData,
    setLatestCloses,
    setLatestBaseCloses,
  ]);

  /* --------------------- Reset on Timeframe/Company Change --------------------- */

  useEffect(() => {
    setCandleData([]);
    setHigh24h(null);
    setLow24h(null);
  }, [companyId, timeframe]);

  /* ------------------------------- Render ------------------------------- */

  return (
    <section className="Main_Chart_Section relative w-full h-full flex flex-col  overflow-hidden z-0">
      <div className="mt-6 -mb-6 z-10 flex items-center justify-between gap-6 text-sm bg-gray-900/60 backdrop-blur-md border border-gray-800 rounded-lg px-5 py-3 shadow-inner">
  <div>
    <span className="text-gray-400 block text-xs">Coin</span>
    <span className="text-white font-semibold">{companyName || "—"}</span>
  </div>

  <div className="h-6 w-px bg-gray-700" /> {/* vertical separator */}

  <div>
    <span className="text-gray-400 block text-xs">24h High</span>
    <span className="text-emerald-400 font-medium">
      {high24h ? high24h.toFixed(4) : "—"}
    </span>
  </div>

  <div>
    <span className="text-gray-400 block text-xs">24h Low</span>
    <span className="text-rose-400 font-medium">
      {low24h ? low24h.toFixed(4) : "—"}
    </span>
  </div>
</div>
      <div className="flex-1 relative min-h-0">
        
        {candleData.length > 0 ? (
          <AmChartsCandleChart
            candleData={candleData}
            showVolume={true} 
            onHoverCandle={() => {}}
          />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-400">
            Loading chart data...
          </div>
        )}
      </div>

      

      {/* <div className="timeframe_buttons_holder z-10">
        <p className="company_name">{companyName}</p>
        <select
          value={timeframe}
          onChange={debounce((e) => setTimeframe(e.target.value))}
        >
          {Object.keys(TIMEFRAMES).map((tf) => (
            <option key={tf} value={tf}>
              {tf}
            </option>
          ))}
        </select>
      </div> */}

      {/* <Successfull_Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        message="Trade Successful!"
        lottie={<Deposit_Lottie />}
      /> */}
    </section>
  );
};

export default Main_Chart_Section;